import { AnyID, Getter } from '@types'
import {
  isKubeBase,
  isKubeCushion,
  isKubeModule,
  isOreja,
  isRinconeroCatalog,
} from '@utils/sceneObjects/items/rinconeroUtils'
import {
  hydrateKubeMeshLocalMins,
  resolveKubeCushionDrawPose,
} from '@utils/modularSnap/kubeCushion'
import { KUBE_SNAP_OVERLAP_MM } from '@constants'
import { Matrix4, Quaternion, Vector3 } from 'three'
import type { DrawItemOptions } from './itemActions'

/**
 * Contexto necesario para calcular la colocación inicial de un item por
 * auto-snap a una unidad existente (padre). Reúne el estado que `drawItem`
 * ya tiene calculado en el momento de decidir dónde colocar el nuevo item.
 */
export type ParentSnapPlacementContext = {
  store: ReturnType<Getter>
  item: any
  catalogID: string
  options?: DrawItemOptions
  sourceCurrentObjectID: AnyID | ''
  globalLastSelectedId: string
  globalLastSelectedAgeMs: number
  recentGlobalLastSelectedId: string
  logDrawParentSnap: (step: string, data?: Record<string, unknown>) => void
}

/**
 * Resultado del cálculo de colocación. `placed` devuelve la transformación
 * final (posición/rotación) y, si procede, el snap pendiente al padre.
 * `abort` indica que la colocación no es válida (lado ocupado, sin candidatos,
 * etc.); el caller debe abortar el dibujo con esa razón.
 */
export type ParentSnapPlacementResult =
  | {
      status: 'placed'
      centerPosition: Vector3
      quaternion: Quaternion
      pendingParentSnap: { anchorId: AnyID; snapSide: 'left' | 'right' } | null
    }
  | { status: 'abort'; reason: string; data?: Record<string, unknown> }

/**
 * Calcula la posición y rotación con la que se coloca un item nuevo cuando se
 * ancla automáticamente a una unidad existente (snap lateral al padre).
 *
 * Contiene toda la lógica de selección de ancla y lado (incluyendo casos de
 * rinconeros/orejas y la preferencia por el ancla seleccionada en UI). No
 * muta la escena: solo decide la transformación final del item.
 */
export const computeParentSnapPlacement = (
  ctx: ParentSnapPlacementContext,
): ParentSnapPlacementResult => {
  const {
    store,
    item,
    catalogID,
    options,
    sourceCurrentObjectID,
    globalLastSelectedId,
    globalLastSelectedAgeMs,
    recentGlobalLastSelectedId,
    logDrawParentSnap,
  } = ctx

  const centerPosition = new Vector3()
  const quaternion = new Quaternion()
  let pendingParentSnap: {
    anchorId: AnyID
    snapSide: 'left' | 'right'
  } | null = null

  // Cojines: sobre la base si hay, pero siempre AL LADO de otros cojines (nunca encima).
  if (isKubeCushion(catalogID)) {
    const resolveSelected = (rawId: unknown) => {
      const id = String(rawId || '')
      if (!id || id === String(item.id)) return null
      const sceneItem = store.items.get(id as AnyID) as any
      return sceneItem && isKubeBase(sceneItem.catalogID) ? sceneItem : null
    }

    const selectedBase =
      resolveSelected(options?.preDrawSelectedAnchorId) ||
      resolveSelected(sourceCurrentObjectID) ||
      resolveSelected(recentGlobalLastSelectedId)

    hydrateKubeMeshLocalMins(store.items as any, store.sceneGLB as Map<any, any>)

    const pose = resolveKubeCushionDrawPose(
      item.id as AnyID,
      item,
      store.items as any,
      selectedBase as any,
    )
    centerPosition.copy(pose.position)
    quaternion.copy(pose.quaternion)

    logDrawParentSnap(selectedBase ? 'cushion-beside-on-base' : 'cushion-free-beside', {
      itemId: String(item.id),
      baseId: selectedBase ? String(selectedBase.id) : undefined,
      y: Number(centerPosition.y.toFixed(2)),
    })
    return { status: 'placed', centerPosition, quaternion, pendingParentSnap: null }
  }

  const toIncrement = (sceneItem: any) => parseInt(String(sceneItem?.increment || ''), 10)
  const hasValidIncrement = (sceneItem: any) => !isNaN(toIncrement(sceneItem))
  const canUseAsParentAnchor = (sceneItem: any) =>
    Boolean(
      sceneItem &&
      (sceneItem.type === 'items' || sceneItem.type === 'linkedItem') &&
      sceneItem.id !== item.id &&
      hasValidIncrement(sceneItem) &&
      sceneItem.visible !== false &&
      (sceneItem as any).canCollide !== false,
    )

  const normalizedDrawingCatalog = String(catalogID || '')
    .trim()
    .toLowerCase()

  const drawingIsOreja = isOreja(normalizedDrawingCatalog)

  const eligibleRegularItems = Array.from(store.items.values()).filter((sceneItem) =>
    canUseAsParentAnchor(sceneItem),
  )
  const sceneItems = Array.from(store.items.values()) as any[]
  const drawingIsOrejaIzquierda = drawingIsOreja && normalizedDrawingCatalog.endsWith('i')
  const drawingIsOrejaDerecha = drawingIsOreja && normalizedDrawingCatalog.endsWith('d')
  const drawingRinconeroSuffix = normalizedDrawingCatalog.slice(-1)
  const drawingIsRinconeroIzquierda = !drawingIsOreja && drawingRinconeroSuffix === 'd'
  const drawingIsRinconeroDerecha = !drawingIsOreja && drawingRinconeroSuffix === 'i'
  const drawingWantsLeftSide = drawingIsOrejaIzquierda || drawingIsRinconeroIzquierda
  const drawingWantsRightSide = drawingIsOrejaDerecha || drawingIsRinconeroDerecha
  const desiredSnapSide: 'left' | 'right' | null = drawingWantsLeftSide
    ? 'left'
    : drawingWantsRightSide
      ? 'right'
      : null
  const enforceCatalogSide = drawingIsOreja && desiredSnapSide != null

  const isSideCompatibleAnchor = (sceneItem: any, side: 'left' | 'right') => {
    const anchorCatalog = String(sceneItem?.catalogID ?? '')
      .trim()
      .toLowerCase()
    if (isOreja(anchorCatalog)) {
      const allowedSide: 'left' | 'right' | null = anchorCatalog.endsWith('d')
        ? 'left'
        : anchorCatalog.endsWith('i')
          ? 'right'
          : null
      return allowedSide == null || allowedSide === side
    }

    if (isRinconeroCatalog(anchorCatalog)) {
      const anchorIsOreja = isOreja(anchorCatalog)
      const anchorSuffix = anchorCatalog.slice(-1)
      const anchorIsLeft = !anchorIsOreja && anchorSuffix === 'd'
      const allowedSide: 'left' | 'right' = anchorIsLeft ? 'left' : 'right'
      return allowedSide === side
    }

    return true
  }

  const isRinconeroPairCompatible = (sceneItem: any) => {
    const anchorCatalog = String(sceneItem?.catalogID ?? '')
      .trim()
      .toLowerCase()
    if (!isRinconeroCatalog(anchorCatalog)) return true
    const anchorIsOreja = isOreja(anchorCatalog)
    const anchorSuffix = anchorCatalog.slice(-1)
    const anchorIsLeft = !anchorIsOreja && anchorSuffix === 'd'
    return anchorIsLeft === drawingIsRinconeroIzquierda
  }

  const findAnchorBySide = (side: 'left' | 'right') => {
    const freeSideCandidates = eligibleRegularItems.filter((sceneItem: any) => {
      const snappedSides =
        (sceneItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {}
      return !snappedSides[side]
    })

    const compatible = freeSideCandidates.filter(
      (sceneItem: any) =>
        isSideCompatibleAnchor(sceneItem, side) && isRinconeroPairCompatible(sceneItem),
    )
    return compatible.length > 0 ? compatible : null
  }

  if (eligibleRegularItems.length === 0) {
    // La matriz inicial ya viene de newGetDataFromGLB (x=1000, rotación frontal).
    // No sumar +1000 otra vez: provocaba un salto visible en la primera unidad.
    if (item.matrix) {
      item.matrix.decompose(centerPosition, quaternion, new Vector3())
    } else {
      centerPosition.set(1000, 0, 0)
      quaternion.setFromAxisAngle(new Vector3(0, 1, 0), Math.PI)
    }
  } else {
    const getLastByIncrement = (arr: any[]) =>
      arr.reduce((prev, curr) => {
        const prevIncrement = parseInt(prev.increment, 10)
        const currIncrement = parseInt(curr.increment, 10)
        return currIncrement > prevIncrement ? curr : prev
      })

    const rightCandidates = findAnchorBySide('right') || []
    const leftCandidates = findAnchorBySide('left') || []
    // Nota: findAnchorBySide ya filtra por lado libre (snappedSides) y por
    // compatibilidad de lado (isSideCompatibleAnchor), así que si una oreja
    // aparece aquí es porque su único lado habilitado sigue libre: debe poder
    // usarse como ancla normalmente, igual que cualquier otro candidato.
    const preferredDefaultSide: 'left' | 'right' = rightCandidates.length > 0 ? 'right' : 'left'
    const searchSide: 'left' | 'right' = desiredSnapSide ?? preferredDefaultSide
    const anchorCandidates: any[] = searchSide === 'right' ? rightCandidates : leftCandidates

    logDrawParentSnap('anchor-candidates', {
      drawingCatalog: normalizedDrawingCatalog,
      drawingIsOreja,
      drawingIsRinconeroIzquierda,
      desiredSnapSide,
      searchSide,
      regularItemsCount: eligibleRegularItems.length,
      anchorCandidatesCount: anchorCandidates.length,
      selectedAnchorId: null,
    })

    if (enforceCatalogSide && anchorCandidates.length === 0) {
      return {
        status: 'abort',
        reason: 'catalog-no-anchor-candidate-on-required-side',
        data: {
          desiredSnapSide,
          drawingCatalog: normalizedDrawingCatalog,
          drawingIsOreja,
        },
      }
    }

    const compatibleSelectedAnchor = false
    const selectedFromLayer = Array.from(store.layers[store.currentLayer].selected.values())[0] as
      AnyID | undefined
    const preferredSelectedAnchorId =
      (sourceCurrentObjectID as AnyID | null | undefined) || selectedFromLayer
    logDrawParentSnap('selected-anchor-context', {
      optionsPreDrawSelectedAnchorId: String(options?.preDrawSelectedAnchorId || ''),
      sourceCurrentObjectID: String(sourceCurrentObjectID || ''),
      selectedFromLayer: String(selectedFromLayer || ''),
      preferredSelectedAnchorId: String(preferredSelectedAnchorId || ''),
      currentObjectIDInStore: String(store.currentObjectID || ''),
      selectedCountInLayer: store.layers[store.currentLayer].selected.size,
      globalLastSelectedId,
      globalLastSelectedAgeMs: Number.isFinite(globalLastSelectedAgeMs)
        ? globalLastSelectedAgeMs
        : null,
      recentGlobalLastSelectedId,
    })
    const selectedAnchorFromStore = preferredSelectedAnchorId
      ? (store.items.get(preferredSelectedAnchorId as AnyID) as any)
      : null
    const selectedAnchorCandidateRaw =
      selectedAnchorFromStore ||
      (preferredSelectedAnchorId
        ? eligibleRegularItems.find(
            (sceneItem: any) => String(sceneItem?.id) === String(preferredSelectedAnchorId),
          )
        : null)
    const selectedAnchorCandidate = selectedAnchorCandidateRaw
    const selectedAnchorIsValid =
      Boolean(selectedAnchorCandidate) && canUseAsParentAnchor(selectedAnchorCandidate)
    const selectedSnappedSides =
      ((selectedAnchorCandidate as any)?.snappedSides as
        Partial<Record<'left' | 'right', string>> | undefined) || {}
    const selectedAnchorId = String((selectedAnchorCandidate as any)?.id ?? '')
    const selectedSceneOccupiedSides: Partial<Record<'left' | 'right', string>> = {}
    if (selectedAnchorCandidate?.matrix) {
      const selectedPos = new Vector3()
      const selectedQuat = new Quaternion()
      selectedAnchorCandidate.matrix.decompose(selectedPos, selectedQuat, new Vector3())
      const selectedXAxis = new Vector3(1, 0, 0).applyQuaternion(selectedQuat)
      const selectedZAxis = new Vector3(0, 0, 1).applyQuaternion(selectedQuat)
      const selectedWidth = Number((selectedAnchorCandidate as any)?.width || 0)
      const SIDE_TOLERANCE_MM = 80
      const DEPTH_ALIGNMENT_TOLERANCE_MM = 120
      const HEIGHT_TOLERANCE_MM = 120

      sceneItems.forEach((candidate: any) => {
        if (!canUseAsParentAnchor(candidate)) return
        if (String(candidate?.id) === selectedAnchorId || String(candidate?.id) === String(item.id))
          return
        if (!candidate?.matrix) return

        const candidatePos = new Vector3()
        candidate.matrix.decompose(candidatePos, new Quaternion(), new Vector3())

        const delta = new Vector3(
          candidatePos.x - selectedPos.x,
          candidatePos.y - selectedPos.y,
          candidatePos.z - selectedPos.z,
        )
        const sideProjection = selectedXAxis.dot(new Vector3(delta.x, 0, delta.z))
        const depthProjection = Math.abs(selectedZAxis.dot(new Vector3(delta.x, 0, delta.z)))
        const heightDelta = Math.abs(delta.y)

        if (depthProjection > DEPTH_ALIGNMENT_TOLERANCE_MM || heightDelta > HEIGHT_TOLERANCE_MM) {
          return
        }

        const expectedRightProjection = selectedWidth
        const expectedLeftProjection = -(candidate.width || 0)

        if (Math.abs(sideProjection - expectedRightProjection) <= SIDE_TOLERANCE_MM) {
          selectedSceneOccupiedSides.right = String(candidate.id)
        }
        if (Math.abs(sideProjection - expectedLeftProjection) <= SIDE_TOLERANCE_MM) {
          selectedSceneOccupiedSides.left = String(candidate.id)
        }
      })
    }
    const selectedRightOccupied = Boolean(
      (selectedSnappedSides.right && String(selectedSnappedSides.right) !== String(item.id)) ||
      selectedSceneOccupiedSides.right ||
      sceneItems.some((sceneItem: any) => {
        if (String(sceneItem?.id) === selectedAnchorId) return false
        const sceneItemSnappedSides =
          (sceneItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {}
        return String(sceneItemSnappedSides.left || '') === selectedAnchorId
      }),
    )
    const selectedLeftOccupied = Boolean(
      (selectedSnappedSides.left && String(selectedSnappedSides.left) !== String(item.id)) ||
      selectedSceneOccupiedSides.left ||
      sceneItems.some((sceneItem: any) => {
        if (String(sceneItem?.id) === selectedAnchorId) return false
        const sceneItemSnappedSides =
          (sceneItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {}
        return String(sceneItemSnappedSides.right || '') === selectedAnchorId
      }),
    )
    const selectedLeftFree = !selectedLeftOccupied
    const selectedRightOccupiedBySnap = Boolean(
      selectedSnappedSides.right && String(selectedSnappedSides.right) !== String(item.id),
    )
    const selectedLeftOccupiedBySnap = Boolean(
      selectedSnappedSides.left && String(selectedSnappedSides.left) !== String(item.id),
    )
    const shouldForceSelectedLeftPlacement =
      !drawingIsOreja &&
      desiredSnapSide == null &&
      Boolean(selectedAnchorCandidate) &&
      selectedRightOccupiedBySnap &&
      !selectedLeftOccupiedBySnap
    logDrawParentSnap('selected-left-force-check', {
      preferredSelectedAnchorId: String(preferredSelectedAnchorId || ''),
      selectedAnchorId,
      selectedRightOccupiedBySnap,
      selectedLeftOccupiedBySnap,
      shouldForceSelectedLeftPlacement,
    })
    const shouldPreferSelectedLeftAnchor =
      shouldForceSelectedLeftPlacement ||
      (!drawingIsOreja &&
        desiredSnapSide == null &&
        selectedAnchorIsValid &&
        selectedRightOccupied &&
        selectedLeftFree &&
        isSideCompatibleAnchor(selectedAnchorCandidate, 'left') &&
        isRinconeroPairCompatible(selectedAnchorCandidate))

    const fallbackAnchor =
      anchorCandidates.length > 0
        ? getLastByIncrement(anchorCandidates as any[])
        : getLastByIncrement(eligibleRegularItems as any[])

    const anchorItem =
      (shouldPreferSelectedLeftAnchor && (selectedAnchorCandidate as any)) ||
      (compatibleSelectedAnchor as any) ||
      fallbackAnchor
    logDrawParentSnap('selected-left-force-result', {
      shouldPreferSelectedLeftAnchor,
      shouldForceSelectedLeftPlacement,
      anchorId: String((anchorItem as any)?.id ?? ''),
    })

    const anchorPos = new Vector3()
    const anchorQuat = new Quaternion()
    anchorItem.matrix.decompose(anchorPos, anchorQuat, new Vector3())
    const anchorXAxis = new Vector3(1, 0, 0).applyQuaternion(anchorQuat)

    let snapSide: 'left' | 'right' = shouldPreferSelectedLeftAnchor
      ? 'left'
      : (desiredSnapSide ?? searchSide)
    logDrawParentSnap('selected-left-force-snap-side', {
      snapSide,
      desiredSnapSide,
      searchSide,
      shouldPreferSelectedLeftAnchor,
      shouldForceSelectedLeftPlacement,
    })
    const directionMultiplier = snapSide === 'left' ? -1 : 1

    const anchorSnappedSides =
      (anchorItem?.snappedSides as Partial<Record<'left' | 'right', string>>) || {}
    const usingSelectedAnchor =
      String((anchorItem as any)?.id ?? '') === selectedAnchorId && Boolean(selectedAnchorId)
    let occupiedLeft = anchorSnappedSides.left
    let occupiedRight = anchorSnappedSides.right
    if (usingSelectedAnchor) {
      if (selectedSceneOccupiedSides.left !== undefined) {
        occupiedLeft = selectedSceneOccupiedSides.left
      }
      if (selectedSceneOccupiedSides.right !== undefined) {
        occupiedRight = selectedSceneOccupiedSides.right
      }
      if (shouldForceSelectedLeftPlacement) {
        occupiedLeft = selectedSnappedSides.left
        occupiedRight = selectedSnappedSides.right
      }
    }
    const sideOccupant = snapSide === 'left' ? occupiedLeft : occupiedRight
    let effectiveDirection = directionMultiplier

    if (enforceCatalogSide && sideOccupant && sideOccupant !== String(item.id)) {
      return {
        status: 'abort',
        reason: 'catalog-required-side-occupied',
        data: {
          desiredSnapSide,
          sideOccupant,
          itemId: String(item.id),
          anchorId: String((anchorItem as any)?.id ?? ''),
          occupiedLeft,
          occupiedRight,
          drawingIsOreja,
        },
      }
    }

    if (
      desiredSnapSide == null &&
      !shouldForceSelectedLeftPlacement &&
      sideOccupant &&
      sideOccupant !== String(item.id)
    ) {
      const otherSide: 'left' | 'right' = snapSide === 'left' ? 'right' : 'left'
      const otherOccupant = otherSide === 'left' ? occupiedLeft : occupiedRight
      if (!otherOccupant || otherOccupant === String(item.id)) {
        effectiveDirection = -directionMultiplier
        snapSide = otherSide
      } else {
        return {
          status: 'abort',
          reason: 'no-free-side-on-anchor',
          data: {
            itemId: String(item.id),
            anchorId: String((anchorItem as any)?.id ?? ''),
            occupiedLeft,
            occupiedRight,
          },
        }
      }
    }

    const anchorCatalog = String(anchorItem?.catalogID ?? '')
      .trim()
      .toLowerCase()
    const isAnchorRinconero = isRinconeroCatalog(anchorCatalog)
    const anchorIsOreja = isOreja(anchorCatalog)
    const anchorSuffix = anchorCatalog.slice(-1)
    const anchorIsRinconeroLeft = isAnchorRinconero && !anchorIsOreja && anchorSuffix === 'd'
    const anchorIsRinconeroRight = isAnchorRinconero && !anchorIsRinconeroLeft

    const newPosition = new Vector3()
    const newQuaternion = anchorQuat.clone()

    if (enforceCatalogSide && snapSide !== desiredSnapSide) {
      return {
        status: 'abort',
        reason: 'catalog-snap-side-mismatch',
        data: {
          desiredSnapSide,
          snapSide,
          itemId: String(item.id),
          anchorId: String((anchorItem as any)?.id ?? ''),
          drawingIsOreja,
        },
      }
    }

    if (anchorIsRinconeroRight && effectiveDirection > 0) {
      newPosition.copy(anchorPos).add(anchorXAxis.clone().multiplyScalar(anchorItem.width || 0))
      const rotationAdjustment = new Quaternion().setFromAxisAngle(
        new Vector3(0, 1, 0),
        -Math.PI / 2,
      )
      newQuaternion.multiply(rotationAdjustment)
      const rinconeroOffset = anchorItem.thickness || 0
      const zAxis = new Vector3(0, 0, 1).applyQuaternion(anchorQuat)
      newPosition.add(zAxis.multiplyScalar(rinconeroOffset))
    } else if (anchorIsRinconeroLeft && effectiveDirection < 0) {
      newPosition.copy(anchorPos)
      const rotationAdjustment = new Quaternion().setFromAxisAngle(
        new Vector3(0, 1, 0),
        Math.PI / 2,
      )
      newQuaternion.multiply(rotationAdjustment)
      const rinconeroOffset = (anchorItem.thickness || 0) + (item.width || 0)
      const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
      newPosition.add(zAxis.multiplyScalar(-rinconeroOffset))
    } else {
      const widthToUse = effectiveDirection > 0 ? anchorItem.width || 0 : item.width || 0
      // Módulos kube: acercarlos un poco (solapamiento) para que parezcan un sofá
      // continuo en vez de quedar a tope. Solo cuando ambos (ancla y nuevo) son kube.
      const kubeOverlap =
        isKubeModule(catalogID) &&
        !isKubeCushion(catalogID) &&
        isKubeModule(anchorItem?.catalogID) &&
        !isKubeCushion(anchorItem?.catalogID)
          ? KUBE_SNAP_OVERLAP_MM
          : 0
      const offsetDistance = effectiveDirection * Math.max(0, widthToUse - kubeOverlap)
      newPosition.copy(anchorPos).add(anchorXAxis.clone().multiplyScalar(offsetDistance))
    }

    newPosition.y = item.y
    centerPosition.copy(newPosition)
    quaternion.copy(newQuaternion)
    logDrawParentSnap('computed-parent-position', {
      itemId: String(item.id),
      anchorId: String((anchorItem as any)?.id ?? ''),
      snapSide,
      desiredSnapSide,
      directionMultiplier,
      effectiveDirection,
      position: {
        x: Number(newPosition.x.toFixed(2)),
        y: Number(newPosition.y.toFixed(2)),
        z: Number(newPosition.z.toFixed(2)),
      },
    })
    pendingParentSnap = {
      anchorId: anchorItem.id as AnyID,
      snapSide,
    }
  }

  return { status: 'placed', centerPosition, quaternion, pendingParentSnap }
}
